import { Button } from '@/components/Button';
import { CautionIcon } from '@/components/Icons';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SyncWarningDialogProps {
  duration: string;
  lastSyncLabel: string;
  onClose: () => void;
}

export const SyncWarningDialog = ({
  duration,
  lastSyncLabel,
  onClose,
}: SyncWarningDialogProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    dialog?.showModal();
    document.body.style.overflow = 'hidden';

    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="m-auto w-[calc(100%_-_2rem)] max-w-lg rounded-xl bg-white p-6 text-gray shadow-custom backdrop:bg-black/60"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-yellow-200">
          <CautionIcon
            aria-hidden="true"
            className="size-7 [&_g]:fill-yellow-800"
          />
        </span>
        <h2 id={titleId} className="text-lg font-bold">
          {t('sync_stale_warning_title')}
        </h2>
      </div>
      <p id={descriptionId} className="text-md">
        {t('sync_stale_warning_message', { duration })}
      </p>
      <p className="mt-3 text-sm text-gray-500">{lastSyncLabel}</p>
      <div className="mt-6 flex justify-end">
        <Button onClick={onClose}>{t('sync_stale_warning_acknowledge')}</Button>
      </div>
    </dialog>
  );
};
